import type { RegisterConfirmItem } from '../services/MendsVisionService';

/**
 * Bloco 4 estados — nunca rotular como executado sem execução real. Um só vocabulário: o rótulo do item é o título do
 * grupo e o termo do resumo (nada de "precisa de esclarecimento" no topo e "aguardando condição" no item).
 */
export function outcomeLabel(
  outcome: string,
  actionKind?: string,
  _reasonCode?: string | null,
): { label: string; tone: string; group: OutcomeGroup } {
  const o = (outcome || '').toLowerCase();
  const kind = (actionKind || '').toLowerCase();
  const isFact = kind === 'register_notes' || kind === 'update_memory' || kind === 'discount_decision';
  const isReturn = kind === 'create_follow_up' || kind.includes('commitment') || kind.includes('follow_up');
  const meta = (group: OutcomeGroup, tone: string) => ({ label: GROUP_LABEL[group], tone, group });

  if (o === 'queued') return meta('aguardando_aprovacao', 'mai-pill mai-pill-wait');
  if (o === 'awaiting_confirmation') return meta('aguardando_confirmacao', 'mai-pill mai-pill-wait');
  if (o === 'blocked') return meta('bloqueado', 'mai-pill mai-pill-high');
  if (o === 'unsupported') return meta('fora_do_registrar', 'mai-pill');
  if (o === 'cancelled') return meta('cancelado', 'mai-pill');
  if (o === 'waiting_condition') return meta('aguardando_condicao', 'mai-pill mai-pill-wait');
  if (o === 'needs_review') return meta('revisao', 'mai-pill mai-pill-wait');
  if (o === 'needs_info' || o === 'existing_needs_info') return meta('esclarecimento', 'mai-pill mai-pill-wait');
  if (o === 'executed') {
    if (isFact) return meta('registrado', 'mai-pill mai-pill-ai');
    if (isReturn) return meta('programado', 'mai-pill mai-pill-ai');
    if (kind === 'cmd:send_email') return meta('enviado', 'mai-pill mai-pill-ai');
    return meta('concluida', 'mai-pill mai-pill-ai');
  }
  if (o === 'undone') return meta('desfeito', 'mai-pill');
  if (o === 'rejected') return meta('rejeitado', 'mai-pill');
  if (o === 'expired') return meta('expirado', 'mai-pill');
  if (o === 'failed') return meta('falhou', 'mai-pill mai-pill-high');
  if (o === 'suppressed' || o === 'skipped') return meta('outros', 'mai-pill');
  return { label: outcome || 'Processada', tone: 'mai-pill', group: 'outros' };
}

export type OutcomeGroup =
  | 'registrado'
  | 'programado'
  | 'enviado'
  | 'aguardando_aprovacao'
  | 'aguardando_confirmacao'
  | 'bloqueado'
  | 'fora_do_registrar'
  | 'cancelado'
  | 'aguardando_condicao'
  | 'esclarecimento'
  | 'revisao'
  | 'concluida'
  | 'desfeito'
  | 'rejeitado'
  | 'expirado'
  | 'falhou'
  | 'processando'
  | 'outros';

/** Rótulo do item (pílula) — singular. */
const GROUP_LABEL: Record<OutcomeGroup, string> = {
  registrado: 'Registrado',
  programado: 'Programado',
  enviado: 'Enviado',
  aguardando_aprovacao: 'Aguardando aprovação',
  aguardando_confirmacao: 'Aguardando sua confirmação',
  bloqueado: 'Bloqueado',
  fora_do_registrar: 'Fora do Registrar',
  cancelado: 'Cancelado',
  aguardando_condicao: 'Aguardando condição',
  esclarecimento: 'Precisa de esclarecimento',
  revisao: 'Precisa de revisão',
  concluida: 'Concluído',
  desfeito: 'Desfeito',
  rejeitado: 'Rejeitado',
  expirado: 'Expirado',
  falhou: 'Falhou',
  processando: 'Processando…',
  outros: 'Não aplicada',
};

/** Título do grupo — o mesmo termo do item. */
export const GROUP_TITLE: Record<OutcomeGroup, string> = {
  ...GROUP_LABEL,
  outros: 'Não aplicadas',
};

/** Resumo só do que o servidor confirmou por item — nunca conta fila como "registrado". */
export function summarizeConfirm(items: RegisterConfirmItem[] | undefined): string {
  const list = items || [];
  const count = (group: OutcomeGroup) =>
    list.filter((i) => outcomeLabel(i.outcome, i.actionKind, i.reasonCode).group === group).length;
  const plural = (n: number, one: string, many: string) => (n === 1 ? `1 ${one}` : `${n} ${many}`);
  const parts: string[] = [];
  const registered = count('registrado') + count('concluida');
  if (registered > 0) parts.push(plural(registered, 'registrado', 'registrados'));
  const sent = count('enviado');
  if (sent > 0) parts.push(plural(sent, 'enviado', 'enviados'));
  const programmed = count('programado');
  if (programmed > 0) parts.push(plural(programmed, 'programado', 'programados'));
  const confirm = count('aguardando_confirmacao');
  if (confirm > 0) parts.push(`${confirm} aguardando sua confirmação`);
  const queued = count('aguardando_aprovacao');
  if (queued > 0) parts.push(`${queued} aguardando aprovação`);
  const condition = count('aguardando_condicao');
  if (condition > 0) parts.push(`${condition} aguardando condição`);
  const clarify = count('esclarecimento');
  if (clarify > 0) parts.push(plural(clarify, 'precisa de esclarecimento', 'precisam de esclarecimento'));
  const review = count('revisao');
  if (review > 0) parts.push(plural(review, 'precisa de revisão', 'precisam de revisão'));
  const blocked = count('bloqueado');
  if (blocked > 0) parts.push(plural(blocked, 'bloqueado', 'bloqueados'));
  const cancelled = count('cancelado');
  if (cancelled > 0) parts.push(plural(cancelled, 'cancelado', 'cancelados'));
  const outside = count('fora_do_registrar');
  if (outside > 0) parts.push(`${outside} fora do Registrar`);
  const failed = count('falhou');
  if (failed > 0) parts.push(plural(failed, 'falhou', 'falharam'));
  const notApplied = count('outros');
  if (notApplied > 0) parts.push(plural(notApplied, 'não aplicada', 'não aplicadas'));
  if (parts.length === 0) return 'Nada foi alterado por este relato.';
  return `${parts.join(' · ')}.`;
}
